import { Variable } from "../../models/interactions/Variable";

export interface VariableTokenIssue {
  type: "unknown_name" | "unknown_id";
  tokenBody: string;
  tokenText: string;
}

export interface VariableTokenOptions {
  allowVariables?: boolean;
  checkVariables?: boolean;
}

type TokenSource = "name" | "id";

export interface VariableTokenSegment {
  type: "text" | "token";
  text: string;
  tokenBody?: string;
  source?: TokenSource;
  isKnown?: boolean;
  variable?: Variable;
}

const TOKEN_REGEX = /\[\$([^\]]+)\]/g;
const GV_PREFIX = "GV_";

function getVariableMaps(variables: Variable[]) {
  const byName = new Map<string, Variable>();
  const byId = new Map<string, Variable>();

  variables.forEach((variable) => {
    byName.set(variable.name, variable);
    byId.set(variable.id, variable);
  });

  return { byName, byId };
}

export function tokenizeVariableString(
  value: string,
  variables: Variable[]
): VariableTokenSegment[] {
  if (!value) return [];

  const { byName, byId } = getVariableMaps(variables);
  const segments: VariableTokenSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  TOKEN_REGEX.lastIndex = 0;

  while ((match = TOKEN_REGEX.exec(value)) !== null) {
    const fullMatch = match[0];
    const tokenBody = match[1];
    const startIndex = match.index;
    const endIndex = startIndex + fullMatch.length;

    if (startIndex > lastIndex) {
      segments.push({
        type: "text",
        text: value.slice(lastIndex, startIndex),
      });
    }

    if (tokenBody.startsWith(GV_PREFIX)) {
      // Support both legacy "[$GV_<id>]" and built-in IDs that already start with "GV_".
      const legacyVariableId = tokenBody.slice(GV_PREFIX.length);
      const variable = byId.get(tokenBody) ?? byId.get(legacyVariableId);
      segments.push({
        type: "token",
        text: fullMatch,
        tokenBody,
        source: "id",
        isKnown: Boolean(variable),
        variable,
      });
    } else {
      const variable = byName.get(tokenBody);
      segments.push({
        type: "token",
        text: fullMatch,
        tokenBody,
        source: "name",
        isKnown: Boolean(variable),
        variable,
      });
    }

    lastIndex = endIndex;
  }

  if (lastIndex < value.length) {
    segments.push({
      type: "text",
      text: value.slice(lastIndex),
    });
  }

  return segments;
}

function collectIssues(
  segments: VariableTokenSegment[],
  checkVariables: boolean
): VariableTokenIssue[] {
  if (!checkVariables) return [];

  return segments
    .filter((segment) => segment.type === "token" && !segment.isKnown)
    .map((segment) => ({
      type: segment.source === "id" ? "unknown_id" : "unknown_name",
      tokenBody: segment.tokenBody!,
      tokenText: segment.text,
    }));
}

function readOptions(options?: VariableTokenOptions) {
  return {
    allowVariables: options?.allowVariables ?? true,
    checkVariables: options?.checkVariables ?? true,
  };
}

export function convertDisplayToStoredTokens(
  value: string,
  variables: Variable[],
  options?: VariableTokenOptions
) {
  const { allowVariables, checkVariables } = readOptions(options);
  if (!allowVariables) {
    return { value, issues: [] as VariableTokenIssue[] };
  }

  const segments = tokenizeVariableString(value, variables);
  const convertedValue = segments
    .map((segment) => {
      if (segment.type === "text") return segment.text;

      if (
        segment.source === "name" &&
        segment.isKnown &&
        segment.variable?.id
      ) {
        if (segment.variable.id.startsWith(GV_PREFIX)) {
          return `[$${segment.variable.id}]`;
        }
        return `[$${GV_PREFIX}${segment.variable.id}]`;
      }

      return segment.text;
    })
    .join("");

  return {
    value: convertedValue,
    issues: collectIssues(segments, checkVariables),
  };
}

export function convertStoredToDisplayTokens(
  value: string,
  variables: Variable[],
  options?: VariableTokenOptions
) {
  const { allowVariables, checkVariables } = readOptions(options);
  if (!allowVariables) {
    return { value, issues: [] as VariableTokenIssue[] };
  }

  const segments = tokenizeVariableString(value, variables);
  const convertedValue = segments
    .map((segment) => {
      if (segment.type === "text") return segment.text;

      if (
        segment.source === "id" &&
        segment.isKnown &&
        segment.variable?.name
      ) {
        return `[$${segment.variable.name}]`;
      }

      return segment.text;
    })
    .join("");

  return {
    value: convertedValue,
    issues: collectIssues(segments, checkVariables),
  };
}

export function convertTokenSyntaxToPlainVariableNames(
  value: string,
  variables: Variable[],
  options?: VariableTokenOptions
) {
  const { allowVariables, checkVariables } = readOptions(options);
  if (!allowVariables) {
    return { value, issues: [] as VariableTokenIssue[] };
  }

  const segments = tokenizeVariableString(value, variables);
  const convertedValue = segments
    .map((segment) => {
      if (segment.type === "text") return segment.text;
      if (segment.isKnown && segment.variable?.name) {
        return segment.variable.name;
      }
      return segment.text;
    })
    .join("");

  return {
    value: convertedValue,
    issues: collectIssues(segments, checkVariables),
  };
}
